import { addMinutes, startOfDay, endOfDay } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { TopLevelFormatterParams } from 'echarts/types/dist/shared';
import { isArray, isNumber } from 'lodash';
import { DEFAULT_DATE_TIME_FORMAT_WITHOUT_ZONE, getComputerFormattedTime, getTimezoneFormattedTime } from '../../../domain/date/date';
import { PRIMARY_COLOR } from '../../../theme';

const DEFAULT_INTERVAL_MINUTES = 15;

export function renderTooltip(params: TopLevelFormatterParams, timezone: string) {
    if (!isArray(params)) return '';

    const usageSeries = params.find(item => item.seriesName === 'Usage');
    const baselineSeries = params.find(item => item.seriesName === 'Baseline');
    const nominationSeries = params.find(item => item.seriesName?.includes('Nomination'));

    const timestamp = isArray(usageSeries?.data) ? usageSeries?.data[0] : null;

    const usageKw = isArray(usageSeries?.data) ? usageSeries?.data[1] : null;
    const baselineKw = isArray(baselineSeries?.data) ? baselineSeries?.data[1] : null;
    const nominationKw = isArray(nominationSeries?.data) ? nominationSeries?.data[1] : null;

    const dateElement = isNumber(timestamp)
        ? `<div style='text-align: center; color: #697475; padding: 0 10px;'>${getTimezoneFormattedTime(new Date(+timestamp), timezone, DEFAULT_DATE_TIME_FORMAT_WITHOUT_ZONE)} ${getComputerFormattedTime(new Date(), 'zzz')}</div>`
        : '';
    const dividerElement = `<div style='margin: 10px 0 10px 0; height: 1px; border-bottom: 1px solid #f0f0f0;'></div>`;

    return `<div style='padding: 10px 0; margin: 0;'>
                ${dateElement}
                ${dividerElement}
                ${makeSeriesElement('Usage', PRIMARY_COLOR, 'solid', usageKw)}
                ${makeSeriesElement('Baseline', '#4A7088', 'solid', baselineKw)}
                ${isNumber(nominationKw) ? makeSeriesElement('Target', '#CE7553', 'solid', nominationKw) : ''}
            </div>`;
}

function makeSeriesElement(name: string, color: string, style: 'dashed' | 'solid', value: any) {
    const formattedValue = isNumber(value) ? new Intl.NumberFormat('en-EN').format(Math.round(value)) : 'N/A';

    return `<div style='padding: 0 10px; display: flex; justify-content: space-between; color: #2e3838; font-size: 14px; font-weight: 400'>
                    <div style='display: flex; justify-content: center; align-items: center'>
                        <span style='display: inline-block; width: 20px; line-height: 14px; border-bottom: 4px ${style} ${color}'></span> 
                        <span style='padding-left: 4px'>${name}:</span>
                    </div>
                    <div style='align-self: flex-end'>${formattedValue} kW</div>
                </div>`;
}

export function generateExpectedInterval(utcEventStart: Date, eventDurationMinutes: number, timezone: string): number[] {
    const { from, to } = getEventDayInLocalTime(utcEventStart, eventDurationMinutes, timezone);

    const expectedIntervals = [];

    let timestamp = new Date(from);

    while (timestamp.valueOf() < to.valueOf()) {
        expectedIntervals.push(timestamp.valueOf());
        timestamp = addMinutes(timestamp, DEFAULT_INTERVAL_MINUTES);
    }

    return expectedIntervals;
}

function getEventDayInLocalTime(utcEventStart: Date, eventDurationMinutes: number, siteTimezone: string) {
    const eventStartZoned = toZonedTime(utcEventStart, siteTimezone);
    const eventEndZoned = toZonedTime(addMinutes(utcEventStart, eventDurationMinutes), siteTimezone);

    const startOfLocalDay = startOfDay(eventStartZoned);
    const endOfLocalDay = endOfDay(eventEndZoned);

    const fromUtc = fromZonedTime(startOfLocalDay, siteTimezone);
    const toUtc = fromZonedTime(endOfLocalDay, siteTimezone);

    return {
        from: fromUtc,
        to: toUtc,
    };
}
